/* eslint-disable react-hooks/exhaustive-deps */
import { HomeOutlined } from '@ant-design/icons';
import { Button, Form, Steps, Alert, message, Breadcrumb } from 'antd';
import CycleSettings from 'components/cycleSettings';
import PageWrapper from 'components/pageWrapper';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createCycle, getCycleDetails, updateCycle } from 'services/api/cycles';
import styles from 'styles/create-cycle.module.scss';
import { MODULES_ITEMS, STEPS } from 'utils/constants';
import CycleModules from 'components/cycleModules';
import dayjs from 'dayjs';
import CycleParticipants from 'components/cycleParticipants';
import pluralize from 'pluralize';

const STEPS_CONTENT = {
  MODULES: {
    title: 'Modules',
    description: 'Select which reviews you want to include',
  },
  PARTICIPANTS: {
    title: 'Participants',
    description: 'Add employees and select managers',
  },
  SETTINGS: {
    title: 'Settings',
    description: 'Set deadlines and reminders',
  },
};

const CreateCycle = () => {
  const [currentStep, setCurrentStep] = useState(STEPS.MODULES);
  const [modules, setModules] = useState(Object.values(MODULES_ITEMS));

  const [loading, setLoading] = useState(false);
  const [settingsForm] = Form.useForm();
  const [cycle, setCycle] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const fetchCycle = () => {
    const cycleId = searchParams.get('cycleid');
    if (cycleId) {
      return getCycleDetails(cycleId).then((res) => {
        setCycle(res.data);
        setModules((mods) =>
          mods.map((m) => ({
            ...m,
            selected: res.data.modules.some((m2) => m2.type === m.type),
            blocks:
              res.data.modules.find((m2) => m2.type === m.type)?.blocks ||
              m.blocks,
          }))
        );
      });
    }
  };

  useEffect(() => {
    const cycleId = searchParams.get('cycleid');
    if (cycleId) {
      fetchCycle().then(() => {
        setCurrentStep(STEPS.PARTICIPANTS);
      });
    } else {
      if (!loading) {
        createCycleFunction();
      }
    }
  }, []);

  const saveModules = (mods) => {
    setLoading(true);
    return updateCycle({
      cycleId: cycle._id,
      modules: mods
        .filter((m) => m.selected)
        .map((m) => ({
          ...m,
          blocks: m.blocks.map((b) => ({
            ...b,
            _id: undefined,
          })),
        })),
    })
      .then(() => {
        setCycle((cycle) => ({
          ...cycle,
          modules: mods.filter((m) => m.selected),
        }));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const createCycleFunction = () => {
    setLoading(true);

    if (cycle) {
      updateCycle({
        cycleId: cycle._id,
        modules: modules
          .filter((m) => m.selected)
          .map((m) => ({
            ...m,
            blocks: m.blocks.map((b) => ({
              ...b,
              _id: undefined,
            })),
          })),
      })
        .then(() => {
          setCycle((cycle) => ({
            ...cycle,
            modules: modules.filter((m) => m.selected),
          }));
          setCurrentStep(STEPS.PARTICIPANTS);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      createCycle({
        name: 'My new cycle',
        modules: modules
          .filter((m) => m.selected)
          .map((m) => ({
            ...m,
            blocks: m.blocks.map((b) => ({
              ...b,
              _id: undefined,
            })),
          })),
        participants: [],
      })
        .then((params) => {
          setCycle(params.data);
          // setCurrentStep(STEPS.PARTICIPANTS);
          searchParams.set('cycleid', params.data._id);
          setSearchParams(searchParams);
        })
        .catch((e) => {
          message.error(e.message);
          console.error('Error creating the cycle', e);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const saveSettings = (values) => {
    updateCycle({
      name: values.cycleName,
      remindersChannels:
        values.remindersChannels === 'BOTH'
          ? ['EMAIL', 'SLACK']
          : [values.remindersChannels],
      remindersTriggers: values.remindersTriggers,
      cycleId: cycle._id,
      ...(values.deadline
        ? { deadline: dayjs(values.deadline).format('YYYY-MM-DD') }
        : {}),
      reviewVisibility: values.reviewVisibility || 'IMMEDIATE',
    })
      .then((res) => {
        message.success('Cycle saved');
        navigate(`/cycles/${cycle._id}`);
      })
      .catch((e) => {
        console.error(e);
        message.error('Error saving cycle');
      });
  };

  const getStepContent = () => {
    switch (currentStep) {
      case STEPS.MODULES:
        const nbModulesSelected = modules.filter((m) => m.selected).length;
        return (
          <div>
            <div className={styles.stepContent}>
              <CycleModules
                showModuleSelect={true}
                modules={modules}
                setModules={(m) => {
                  setModules(m);
                  saveModules(m);
                }}
                showPreview={true}
              />
            </div>
            <div className={styles.actions}>
              <div className={styles.modulesSelected}>
                {nbModulesSelected > 0
                  ? `${nbModulesSelected} ${pluralize(
                      'module',
                      nbModulesSelected
                    )} selected`
                  : 'No modules selected'}
              </div>
              <Button
                type="primary"
                onClick={() => {
                  createCycleFunction();
                }}
                className={styles.nextButton}
              >
                Next
              </Button>
            </div>
          </div>
        );
      case STEPS.PARTICIPANTS:
        return (
          <div>
            <div className={styles.stepContent}>
              <Alert
                message="They won’t receive any email or notification at this stage."
                description={
                  <div>
                    They will only get messages once you set the review cycle to
                    Live, we’ll tell you when notifications get sent.
                  </div>
                }
                type="info"
                showIcon
              />
              <br />
              <CycleParticipants cycle={cycle} fetchCycle={fetchCycle} />
            </div>
            <div className={styles.actions}>
              <Button
                type="link"
                onClick={() => {
                  setCurrentStep(STEPS.MODULES);
                }}
                className={styles.backButton}
              >
                Back
              </Button>
              <Button
                className={styles.nextButton}
                type="primary"
                onClick={() => {
                  setCurrentStep(STEPS.SETTINGS);
                }}
              >
                Next
              </Button>
            </div>
          </div>
        );
      case STEPS.SETTINGS:
        return (
          <div>
            <div className={styles.stepContent}>
              <CycleSettings formRef={settingsForm} onFinish={saveSettings} />
              <div className={styles.actions}>
                <Button
                  type="link"
                  className={styles.backButton}
                  onClick={() => {
                    setCurrentStep(STEPS.PARTICIPANTS);
                  }}
                >
                  Back
                </Button>

                <Button
                  className={styles.nextButton}
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  onClick={() => {
                    settingsForm.submit();
                  }}
                >
                  View Draft
                </Button>
              </div>
            </div>
          </div>
        );
      default:
    }
    return <></>;
  };

  const currentStepIndex = Object.keys(STEPS).findIndex(
    (step) => step === currentStep
  );

  return (
    <PageWrapper width={760}>
      <Breadcrumb
        items={[
          {
            title: (
              <a href="/#/dashboard">
                <HomeOutlined />
                &nbsp;Dashboard
              </a>
            ),
          },
          {
            title: 'Create Cycle',
          },
        ]}
      />
      <br />
      <br />
      <div className={styles.createCycleWrapper}>
        <div className={styles.createCycleContent}>
          <h1>Create a new Cycle</h1>
          <br />
          <br />
          <br />
          <div className={styles.stepper}>
            <Steps
              current={currentStepIndex}
              items={Object.values(STEPS_CONTENT)}
            />
          </div>
          <div>{getStepContent()}</div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default CreateCycle;
